import { styled, GetProps, XStack, YStack, Text } from 'tamagui'
import type { ReactNode } from 'react'

// Mobile: stacked layout (YStack), Desktop: horizontal (XStack via flexDirection)
const SummaryBarFrame = styled(YStack, {
  name: 'SummaryBar',
  
  // Fixed bottom bar styling
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '$background',
  borderTopWidth: 2,
  borderColor: '$borderColor',
  paddingVertical: '$3',
  paddingHorizontal: '$4',
  gap: '$3',
  
  // Safe area padding for mobile
  paddingBottom: '$4',
  
  // Desktop: single row
  $gtMd: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '$4',
  },
})

// Top row contains brand + details (mobile), or just flows inline (desktop)
const SummaryBarTopRow = styled(XStack, {
  name: 'SummaryBarTopRow',
  alignItems: 'center',
  gap: '$3',
  flexWrap: 'wrap',
  
  $gtMd: {
    flex: 1,
    gap: '$4',
  },
})

const SummaryBarBrand = styled(Text, {
  name: 'SummaryBarBrand',
  fontSize: 10,
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 2,
  color: '$colorMuted',
  display: 'none',
  
  $gtSm: {
    display: 'flex',
  },
})

const SummaryBarDetails = styled(XStack, {
  name: 'SummaryBarDetails',
  gap: '$3',
  flex: 1,
  flexWrap: 'wrap',
  
  $gtMd: {
    gap: '$4',
    flexWrap: 'nowrap',
  },
})

const SummaryBarItem = styled(YStack, {
  name: 'SummaryBarItem',
  alignItems: 'flex-start',
})

const SummaryBarItemLabel = styled(Text, {
  name: 'SummaryBarItemLabel',
  fontSize: 9,
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 1,
  color: '$colorMuted',
  
  $gtMd: {
    fontSize: 10,
  },
})

const SummaryBarItemValue = styled(Text, {
  name: 'SummaryBarItemValue',
  fontSize: 12,
  fontWeight: '700',
  color: '$color',
  
  $gtMd: {
    fontSize: 14,
  },
})

// Bottom row contains total + action (mobile), or just flows inline (desktop)
const SummaryBarBottomRow = styled(XStack, {
  name: 'SummaryBarBottomRow',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$3',
  
  $gtMd: {
    gap: '$4',
  },
})

const SummaryBarTotal = styled(YStack, {
  name: 'SummaryBarTotal',
  alignItems: 'flex-start',
  
  $gtMd: {
    alignItems: 'flex-end',
  },
})

const SummaryBarTotalLabel = styled(Text, {
  name: 'SummaryBarTotalLabel',
  fontSize: 9,
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 1,
  color: '$colorMuted',
  
  $gtMd: {
    fontSize: 10,
  },
})

const SummaryBarTotalValue = styled(Text, {
  name: 'SummaryBarTotalValue',
  fontSize: 18,
  fontWeight: '800',
  color: '$accent',
  
  $gtMd: {
    fontSize: 20,
  },
})

const SummaryBarAction = styled(YStack, {
  name: 'SummaryBarAction',
  flex: 1,
  
  $gtMd: {
    flex: 0,
  },
})

export interface SummaryBarItem {
  label: string
  value: string
}

export interface SummaryBarProps extends GetProps<typeof SummaryBarFrame> {
  items?: SummaryBarItem[]
  total: {
    label: string
    value: string
  }
  action: ReactNode
  brand?: string
}

export function SummaryBar({
  items = [],
  total,
  action,
  brand,
  ...props
}: SummaryBarProps) {
  return (
    <SummaryBarFrame {...props}>
      {/* Top row: Brand + Details */}
      <SummaryBarTopRow>
        {brand && <SummaryBarBrand>{brand}</SummaryBarBrand>}
        
        {items.length > 0 && (
          <SummaryBarDetails>
            {items.map((item, index) => (
              <SummaryBarItem key={index}>
                <SummaryBarItemLabel>{item.label}</SummaryBarItemLabel>
                <SummaryBarItemValue>{item.value}</SummaryBarItemValue>
              </SummaryBarItem>
            ))}
          </SummaryBarDetails>
        )}
      </SummaryBarTopRow>
      
      {/* Bottom row: Total + Action */}
      <SummaryBarBottomRow>
        <SummaryBarTotal>
          <SummaryBarTotalLabel>{total.label}</SummaryBarTotalLabel>
          <SummaryBarTotalValue>{total.value}</SummaryBarTotalValue>
        </SummaryBarTotal>
        
        <SummaryBarAction>
          {action}
        </SummaryBarAction>
      </SummaryBarBottomRow>
    </SummaryBarFrame>
  )
}

SummaryBar.displayName = 'SummaryBar'
